<template>
  <div class="account-manager" :class="{ waiting: waiting }">
    <span v-if="!user.loggedIn">
      <b-btn v-b-modal.account @click="show('register')" size="lg">Register</b-btn>
      <b-btn v-b-modal.account @click="show('login')" size="lg" variant="primary">Login</b-btn>
    </span>
    <span v-else>
      <b-btn v-b-modal.account @click="show('update')" size="lg" variant="primary">Account</b-btn>
    </span>
    <a v-b-popover="popoverConfig" class="info-button p-2">
      <i class="fa fa-info-circle" />
    </a>
    <span>
      <b-btn @click.prevent="handleReload()" variant="default" size="lg">
        <i class="fa fa-undo" /> &nbsp;
        Start Over
      </b-btn>
    </span>

    <b-modal
      ref="account"
      id="account"
      :title="title"
      @shown="$refs.focus.focus()"
    >
      <b-form ref="form" @submit.prevent="handleSubmit()">
        <aside
          v-for="alert in alerts"
          :key="alert.message"
          :class="`alert alert-${alert.type || 'success'}`"
          v-html="alert.message"
        />

        <b-form-group label="Email" label-for="account-email">
          <b-form-input
            id="account-email"
            type="email"
            name="email"
            v-model="credentials.email"
            required
            autocomplete="email"
            ref="focus"
          />
        </b-form-group>
        <b-form-group v-if="mode !== 'forgot'" label="Password" label-for="account-password">
          <password-input
            id="account-password"
            name="password"
            v-model="credentials.password"
            autocomplete="current-password"
            :required="mode !== 'update' || !!credentials.newPassword"
          />
        </b-form-group>
        <b-form-group v-if="mode === 'update'" label="New Password" label-for="account-newPassword">
          <password-input
            id="account-newPassword"
            name="newPassword"
            v-model="credentials.newPassword"
            autocomplete="new-password"
            placeholder="Leave blank to leave password unchanged"
          />
        </b-form-group>
        <div v-if="mode === 'register' || mode === 'update'">
          <b-form-group label="First Name" label-for="account-firstName">
            <b-form-input
              id="account-firstName"
              type="text"
              name="firstName"
              v-model="profile.firstName"
              autocomplete="given-name"
              required
            />
          </b-form-group>
          <b-form-group label="Last Name" label-for="account-lastName">
            <b-form-input
              id="account-lastName"
              type="text"
              name="lastName"
              v-model="profile.lastName"
              autocomplete="family-name"
              required
            />
          </b-form-group>
          <b-form-group label="Postal / Zip Code" label-for="account-postCode">
            <b-form-input
              id="account-postCode"
              type="text"
              name="postCode"
              v-model="profile.postCode"
              autocomplete="postal-code"
              required
            />
          </b-form-group>
        </div>

        <footer>
          <b-btn type="submit" hidden><!-- allows form to be submit via enterpress --></b-btn>
          <b-btn v-if="mode === 'login'" @click="show('forgot')">Forgot your password?</b-btn>
          <b-btn v-if="mode === 'forgot'" @click="show('login')">Nevermind, I remember</b-btn>
        </footer>
      </b-form>
      <spinner v-if="waiting" />

      <b-btn slot="modal-footer" v-if="mode === 'login'" variant="light" @click="show('register')">
        Don't have an account?
      </b-btn>
      <b-btn slot="modal-footer" v-if="mode === 'register'" variant="light" @click="show('login')">
        Already have an account?
      </b-btn>
      <b-btn slot="modal-footer" v-if="mode === 'update'" variant="light" @click="logout()">
        Logout
      </b-btn>

      <b-btn slot="modal-footer" @click="hide()" class="ml-auto">
        Cancel
      </b-btn>
      <b-btn slot="modal-footer" variant="primary" @click.prevent="handleSubmit()">
        {{ okTitle }}
      </b-btn>
    </b-modal>
  </div>
</template>

<script>
import PasswordInput from '@/components/utility/PasswordInput';
import Spinner from '@/components/utility/Spinner';

export default {
  name: 'account-manager',
  data() {
    return {
      mode: 'login',
      user: window.Renoworks.User,
      credentials: {
        email: undefined,
        password: undefined,
        newPassword: undefined,
      },
      profile: {
        firstName: undefined,
        lastName: undefined,
        postCode: undefined,
      },
      waiting: false,
      alerts: [],

      popoverConfig: {
        trigger: 'hover',
        placement: 'top',
        content: 'Begin designing your door, register to save your project, or login to access saved projects.',
      },
    };
  },
  computed: {
    title() {
      switch (this.mode) {
        case 'register': {
          return 'Register';
        }
        case 'forgot': {
          return 'Forgotten Password';
        }
        case 'reset': {
          return 'Reset Password';
        }
        default:
        case 'login': {
          return 'Login';
        }
        case 'update': {
          return 'Account';
        }
      }
    },
    okTitle() {
      switch (this.mode) {
        case 'update': {
          return 'Save';
        }
        case 'forgot': {
          return 'Submit';
        }
        case 'reset': {
          return 'Change Password';
        }
        default: {
          return this.title;
        }
      }
    },
  },
  watch: {
    user: {
      // pluck user data out from CF model
      handler(user) {
        this.$set(this, 'credentials', { email: user.email });
        this.$set(this, 'profile', user.getData());
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    hide() {
      this.$refs.account.hide();

      // remove reset key from query string, if necessary
      if (this.$route.query.reset) {
        const query = { ...this.$route.query };
        delete query.reset;
        this.$router.replace({ query });
      }
    },
    show(mode = undefined) {
      this.clear();

      if (mode) {
        this.mode = mode;
      }

      this.$refs.account.show();
    },
    clear() {
      this.alerts = [];
    },
    alert(alert, clear = true) {
      if (typeof alert === 'string') {
        alert = {
          message: alert,
        };
      }
      if (clear) this.clear();
      this.alerts.push(alert);
    },
    handleError(message, clear = true) {
      this.alert({
        type: 'danger',
        message: message.toString().replace(/^ERROR:\s*/, ''),
      }, clear);
    },
    handleReload() {
      this.$root.confirm('You will lose all unsaved changes (if any).').then(() => {
        window.location.reload();
      });
    },
    async await(fn) {
      this.waiting = true;
      this.clear();
      try {
        await fn.call(this);
      } catch (err) {
        this.handleError(err);
      }
      this.waiting = false;
    },

    register() {
      this.await(async () => {
        await window.Renoworks.User
          .create(this.credentials.email, this.credentials.password, this.profile);
        this.hide();
      });
    },
    forgot() {
      this.await(async () => {
        await window.Renoworks.User.resetPassword(this.credentials.email);
        this.alert('Check your inbox for a link to reset your password.');
      });
    },
    reset() {
      this.await(async () => {
        await window.Renoworks.User
          .changePassword(this.credentials.email, this.credentials.password);
        this.alert('Your password has been reset.');
      });
    },
    login() {
      this.await(async () => {
        const { email, password } = this.credentials;
        await window.Renoworks.User.signIn(email, password);
        this.hide();
      });
    },
    update() {
      this.await(async () => {
        // only change password if newPassword is set
        const password = this.credentials.newPassword ? this.credentials.password : null;
        const newPassword = this.credentials.newPassword || null;

        await window.Renoworks.User
          .update(this.credentials.email, password, newPassword, this.profile);
        this.alert('Account details successfully updated.');
      });
    },
    logout() {
      this.await(async () => {
        await window.Renoworks.User.signOut();
        this.hide();
      });
    },

    handleSubmit() {
      const isValid = this.$refs.form.reportValidity();
      if (!isValid) return this.handleError('Invalid or incomplete form entries.');

      const handler = this[this.mode];
      return handler.call(this);
    },
  },
  async created() {
    // keep Vue's instance up-to-date with global/unobservable object
    window.jQuery(window.Renoworks.Event).on(window.Renoworks.Event.USER_LOADED, () => {
      this.$set(this, 'user', window.Renoworks.User);
    });

    // initialize session and get initial user state
    window.Renoworks.Session.initialize();
    await window.Renoworks.User.checkSignedIn();
  },
  mounted() {
    // open password reset modal automatically if reset key is specified
    const resetKey = this.$route.query.reset;
    if (resetKey) {
      window.Renoworks.User.reset = resetKey;

      this.show('reset');
      this.alert({
        type: 'info',
        message: 'Enter your <em>existing email</em>, then pick your <em>new password</em>.',
      });
    }
  },
  components: {
    PasswordInput,
    Spinner,
  },
};
</script>

<style lang="scss">
.account-manager {
  .modal-footer {
    flex-wrap: wrap;
  }

  &.waiting {
    form {
      opacity: 0.5;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/AccountManager.vue